import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { appModules } from "../components/global";
import { GET, POST, PUT, loading, alert, format_date, Select, Pagination, DateInput } from "../components/CustomComponent";

const sectionCardStyle = {
  background: "#ffffff",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  borderRadius: 24,
  boxShadow: "0 14px 32px rgba(12, 118, 229, 0.06)",
};

const summaryCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)",
  border: "1px solid rgba(18, 125, 255, 0.10)",
  boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)",
};

const modalInputStyle = {
  borderRadius: 12,
  border: "1px solid #c9d7e3",
  background: "#ffffff",
  color: "#1b3950",
  boxShadow: "none",
};

const modalLabelStyle = {
  color: "#0b4f84",
  fontSize: "0.82rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "pending", label: "รออนุมัติ" },
  { value: "approved", label: "อนุมัติแล้ว" },
  { value: "rejected", label: "ไม่อนุมัติ" },
  { value: "cancelled", label: "ยกเลิก" },
];

const STATUS_STYLE = {
  pending: { background: "rgba(245, 166, 35, 0.16)", color: "#b97007" },
  approved: { background: "rgba(47, 181, 114, 0.14)", color: "#188251" },
  rejected: { background: "rgba(229, 83, 83, 0.16)", color: "#b63636" },
  cancelled: { background: "rgba(108, 122, 137, 0.14)", color: "#566575" },
};

const HALF_DAY_OPTIONS = [
  { value: "none", label: "ลาเต็มวัน" },
  { value: "morning", label: "ลาครึ่งวันเช้า" },
  { value: "afternoon", label: "ลาครึ่งวันบ่าย" },
];

const leaveFormDefaults = {
  uid: "",
  employee_id: "",
  leave_type: "annual",
  date_from: "",
  date_to: "",
  half_day: "none",
  reason: "",
};

const statusLabel = (status) => STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "-";
const statusStyle = (status) => STATUS_STYLE[status] || STATUS_STYLE.cancelled;

const getEmployeeName = (employee) => {
  if (!employee) return "-";
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return fullName || employee.nickname || employee.employee_code || "-";
};

const LeaveModal = ({ show, onHide, editData, onSave, leaveTypes = [], employeeOptions = [], canManageAll = false }) => {
  const [formData, setFormData] = useState(leaveFormDefaults);

  useEffect(() => {
    setFormData({
      ...leaveFormDefaults,
      uid: editData?.uid || "",
      employee_id: editData?.employee?.uid || "",
      leave_type: editData?.leave_type || "annual",
      date_from: editData?.date_from || "",
      date_to: editData?.date_to || "",
      half_day: editData?.half_day || "none",
      reason: editData?.reason || "",
    });
  }, [editData, show]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => {
      const next = { ...current, [name]: value };
      // เลือกวันเริ่มแล้วยังไม่มีวันสิ้นสุด หรือวันสิ้นสุดอยู่ก่อนวันเริ่ม ให้ตามวันเริ่มไปก่อน
      if (name === "date_from" && (!current.date_to || current.date_to < value)) next.date_to = value;
      // ลาครึ่งวันต้องเป็นวันเดียว
      if (name === "half_day" && value !== "none") next.date_to = next.date_from;
      return next;
    });
  };

  const isHalfDay = formData.half_day !== "none";
  const selectedType = leaveTypes.find((type) => type.value === formData.leave_type);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="border-0 shadow-lg overflow-hidden" dialogClassName="modal-dialog-scrollable">
      <Modal.Header
        closeButton
        className="border-0 p-4"
        style={{
          background: "radial-gradient(circle at top right, rgba(38, 218, 222, 0.28), transparent 24%), linear-gradient(135deg, #d9eeff 0%, #bfe1ff 44%, #e7f6ff 100%)",
        }}
      >
        <Modal.Title className="fw-bold" style={{ color: "#11324c", fontSize: "1.6rem" }}>
          {editData ? "แก้ไขคำขอลา" : "ยื่นคำขอลา"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4" style={{ background: "linear-gradient(180deg, #fcfeff 0%, #f5faff 100%)" }}>
        <div className="row g-3">
          {canManageAll && !editData && (
            <div className="col-12">
              <label className="form-label" style={modalLabelStyle}>ยื่นในนามพนักงาน</label>
              <Select
                className="form-control"
                value={formData.employee_id}
                options={employeeOptions}
                placeholder="ตัวเอง (เว้นว่างไว้)"
                onChange={(value) => setFormData((current) => ({ ...current, employee_id: value || "" }))}
              />
            </div>
          )}

          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ประเภทการลา</label>
            <select name="leave_type" className="form-select" style={modalInputStyle} value={formData.leave_type} onChange={handleChange}>
              {leaveTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {selectedType?.quota ? (
              <div className="small mt-1 text-secondary">โควตา {selectedType.quota} วัน/ปี</div>
            ) : (
              <div className="small mt-1 text-secondary">ไม่จำกัดโควตา แต่ต้องได้รับอนุมัติ</div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>รูปแบบการลา</label>
            <select name="half_day" className="form-select" style={modalInputStyle} value={formData.half_day} onChange={handleChange}>
              {HALF_DAY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ตั้งแต่วันที่</label>
            <DateInput name="date_from" className="form-control" style={modalInputStyle} value={formData.date_from} onChange={handleChange} />
          </div>

          <div className="col-md-6">
            <label className="form-label" style={modalLabelStyle}>ถึงวันที่</label>
            <DateInput
              name="date_to"
              className="form-control"
              style={modalInputStyle}
              value={formData.date_to}
              min={formData.date_from || undefined}
              disabled={isHalfDay}
              onChange={handleChange}
            />
            {isHalfDay && <div className="small mt-1 text-secondary">ลาครึ่งวันต้องเป็นวันเดียว</div>}
          </div>

          <div className="col-12">
            <label className="form-label" style={modalLabelStyle}>เหตุผลการลา</label>
            <textarea name="reason" className="form-control" style={modalInputStyle} rows={3} value={formData.reason} onChange={handleChange} placeholder="ระบุเหตุผล" />
          </div>

          <div className="col-12">
            <div className="rounded-3 p-3 small" style={{ background: "rgba(12,117,230,0.06)", color: "#2d6288" }}>
              ระบบนับจำนวนวันลาให้อัตโนมัติ โดยตัดวันเสาร์-อาทิตย์ และวันหยุดบริษัทตามปฏิทินในหน้าตั้งค่าออกแล้ว
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 py-4" style={{ background: "linear-gradient(180deg, rgba(243,249,255,0.84) 0%, rgba(248,252,255,0.98) 100%)" }}>
        <div className="d-flex flex-column flex-sm-row gap-3 w-100 justify-content-end">
          <button type="button" className="btn btn-secondary px-4 py-2 rounded-4" onClick={onHide}>ยกเลิก</button>
          <button type="button" className="btn btn-primary px-4 py-2 rounded-4" onClick={() => onSave(formData)}>ส่งคำขอ</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default function Leave() {
  const { title, description } = appModules["leave"];

  const profile = useMemo(() => {
    try {
      const raw = localStorage.getItem("ptn-profile");
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }, []);

  // มีสิทธิ์ leaves.update = เห็นและอนุมัติใบลาของทุกคน ไม่มี = เห็นเฉพาะของตัวเอง
  const canManageAll = (profile?.role?.permission || []).includes("leaves.update");

  const [data, setData] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [summary, setSummary] = useState({ types: [], total_used: 0 });
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const [statusInput, setStatusInput] = useState("");
  const [typeInput, setTypeInput] = useState("");
  const [employeeInput, setEmployeeInput] = useState("");
  const [filters, setFilters] = useState({ status: "", leave_type: "", employee_id: "" });

  const getData = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/leaves", {
      page,
      page_size: PAGE_SIZE,
      year,
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.leave_type ? { leave_type: filters.leave_type } : {}),
      ...(filters.employee_id ? { employee_id: filters.employee_id } : {}),
    });

    if (result?.status) {
      setData(result.data || []);
      setPageCount(Math.max(1, Math.ceil((result.pagination?.count || 0) / PAGE_SIZE)));
    }
    loading(false);
  }, [page, year, filters]);

  const getSummary = useCallback(async () => {
    const result = await GET("v1/admin/leaves/summary", { year });
    if (result?.status && result.data) setSummary(result.data);
  }, [year]);

  useEffect(() => {
    const loadStatic = async () => {
      const typeResult = await GET("v1/admin/leaves/types");
      if (typeResult?.status) setLeaveTypes(typeResult.data || []);

      if (canManageAll) {
        const employeeResult = await GET("v1/admin/employees", { page: 0 });
        if (employeeResult?.status) setEmployees(employeeResult.data || []);
      }
    };
    loadStatic();
  }, [canManageAll]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    getSummary();
  }, [getSummary]);

  const handleOpenModal = (item = null) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditData(null);
    setShowModal(false);
  };

  const handleSave = async (formData) => {
    if (!formData.date_from || !formData.date_to) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาเลือกวันที่เริ่มและวันที่สิ้นสุด");
      return;
    }
    if (formData.date_from > formData.date_to) {
      await alert("", "warning", "แจ้งเตือน", "วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด");
      return;
    }
    if (!(await alert("", "info", "ยืนยัน", "ต้องการส่งคำขอลานี้?", "ส่งคำขอ", "ยกเลิก"))) return;

    loading(true);
    const body = {
      leave_type: formData.leave_type,
      date_from: formData.date_from,
      date_to: formData.date_to,
      half_day: formData.half_day,
      reason: formData.reason?.trim() || undefined,
      ...(formData.uid ? {} : { employee_id: formData.employee_id || undefined }),
    };

    const result = formData.uid
      ? await PUT(`v1/admin/leaves/${formData.uid}`, body)
      : await POST("v1/admin/leaves", body);
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "บันทึกคำขอลาเรียบร้อย");
      handleCloseModal();
      getData();
      getSummary();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "บันทึกไม่สำเร็จ");
    }
  };

  const handleApprove = async (item) => {
    if (!(await alert("", "info", "อนุมัติคำขอลา", `อนุมัติการลาของ ${getEmployeeName(item.employee)} ?`, "อนุมัติ", "ยกเลิก"))) return;

    loading(true);
    const result = await PUT(`v1/admin/leaves/${item.uid}/approve`, {});
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "อนุมัติเรียบร้อย");
      getData();
      getSummary();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "อนุมัติไม่สำเร็จ");
    }
  };

  const handleReject = async (item) => {
    const reason = window.prompt("ระบุเหตุผลที่ไม่อนุมัติ");
    if (reason === null) return;
    if (!reason.trim()) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาระบุเหตุผล");
      return;
    }

    loading(true);
    const result = await PUT(`v1/admin/leaves/${item.uid}/reject`, { rejection_reason: reason.trim() });
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "บันทึกการไม่อนุมัติเรียบร้อย");
      getData();
      getSummary();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ดำเนินการไม่สำเร็จ");
    }
  };

  const handleCancel = async (item) => {
    if (!(await alert("", "warning", "ยกเลิกคำขอลา", `ต้องการยกเลิกคำขอ ${item.leave_code || ""} ?`, "ยกเลิกคำขอ", "ปิด"))) return;

    loading(true);
    const result = await PUT(`v1/admin/leaves/${item.uid}/cancel`, {});
    loading(false);

    if (result?.status) {
      await alert("", "success", "สำเร็จ", "ยกเลิกคำขอเรียบร้อย");
      getData();
      getSummary();
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "ยกเลิกไม่สำเร็จ");
    }
  };

  const handleSearch = () => {
    setPage(1);
    setFilters({ status: statusInput, leave_type: typeInput, employee_id: employeeInput });
  };

  const handleReset = () => {
    setStatusInput("");
    setTypeInput("");
    setEmployeeInput("");
    setPage(1);
    setFilters({ status: "", leave_type: "", employee_id: "" });
  };

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const list = [];
    for (let y = current; y >= current - 3; y -= 1) list.push({ value: String(y), label: `ปี ${y}` });
    return list;
  }, []);

  const typeOptions = leaveTypes.map((type) => ({ value: type.value, label: type.label }));
  const employeeOptions = employees.map((employee) => ({
    value: employee.uid,
    label: `${getEmployeeName(employee)}${employee.employee_code ? ` (${employee.employee_code})` : ""}`,
  }));

  const pendingCount = data.filter((item) => item.status === "pending").length;

  // การ์ดสรุปโควตา แสดงเฉพาะประเภทที่มีโควตาจำกัด
  const quotaCards = (summary.types || []).filter((type) => type.quota !== null);

  return (
    <AppShell title={title} description={description}>
      <div className="row g-3 mb-4">
        {quotaCards.map((type) => {
          const used = type.used_days || 0;
          const percent = type.quota ? Math.min(100, (used / type.quota) * 100) : 0;
          const nearLimit = type.quota && used >= type.quota;
          return (
            <div className="col-6 col-xl-3" key={type.value}>
              <div className="h-100 rounded-4 p-4" style={summaryCardStyle}>
                <p className="small text-uppercase text-secondary mb-1">{type.label}</p>
                <h3 className="h4 mb-1 fw-bold" style={{ color: nearLimit ? "#b63636" : "#0c76e5" }}>
                  {used} <span className="text-secondary" style={{ fontSize: "0.9rem", fontWeight: 500 }}>/ {type.quota} วัน</span>
                </h3>
                <div className="progress" style={{ height: 5, background: "rgba(15,102,182,0.08)" }}>
                  <div className="progress-bar" style={{ width: `${percent}%`, background: nearLimit ? "#b63636" : "#0c76e5" }} />
                </div>
                <div className="small text-secondary mt-2">คงเหลือ {type.remaining_days} วัน</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack">
          <div style={{ width: "130px" }}>
            <Select className="form-control" value={year} options={yearOptions} placeholder="เลือกปี" onChange={(value) => setYear(value)} />
          </div>
          <div style={{ width: "160px" }}>
            <Select className="form-control" value={statusInput} options={STATUS_OPTIONS} placeholder="ทุกสถานะ" onChange={(value) => setStatusInput(value)} />
          </div>
          <div style={{ width: "170px" }}>
            <Select className="form-control" value={typeInput} options={typeOptions} placeholder="ทุกประเภท" onChange={(value) => setTypeInput(value)} />
          </div>
          {canManageAll && (
            <div style={{ width: "210px" }}>
              <Select className="form-control" value={employeeInput} options={employeeOptions} placeholder="ทุกคน" onChange={(value) => setEmployeeInput(value)} />
            </div>
          )}
          <button type="button" className="btn btn-primary px-4" onClick={handleSearch} style={{ borderRadius: "12px" }}>ค้นหา</button>
          <button
            type="button"
            className="btn btn-light px-4"
            onClick={handleReset}
            style={{ borderRadius: "12px", border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
          >
            ล้างค่า
          </button>
        </div>
        <button onClick={() => handleOpenModal()} className="btn btn-primary px-4 flex-shrink-0" style={{ borderRadius: "12px" }}>
          + ยื่นคำขอลา
        </button>
      </div>

      <div className="p-3 p-md-4" style={sectionCardStyle}>
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
          <h5 className="mb-0 fw-semibold text-dark">รายการคำขอลา</h5>
          {pendingCount > 0 && (
            <span className="badge rounded-pill px-3 py-2" style={{ background: "rgba(245, 166, 35, 0.16)", color: "#b97007" }}>
              รออนุมัติ {pendingCount} รายการ
            </span>
          )}
        </div>

        <div className="table-responsive table-mobile-cards">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium">รหัส</th>
                {canManageAll && <th className="text-secondary fw-medium">พนักงาน</th>}
                <th className="text-secondary fw-medium text-nowrap">ประเภท</th>
                <th className="text-secondary fw-medium text-nowrap">ช่วงวันที่</th>
                <th className="text-secondary fw-medium text-center text-nowrap">จำนวนวัน</th>
                <th className="text-secondary fw-medium">เหตุผล</th>
                <th className="text-secondary fw-medium text-center text-nowrap">สถานะ</th>
                <th className="text-secondary fw-medium text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item) => {
                  const typeLabel = leaveTypes.find((type) => type.value === item.leave_type)?.label || item.leave_type;
                  return (
                    <tr key={item.uid}>
                      <td data-label="รหัส" className="fw-semibold text-nowrap" style={{ color: "#0b4f84" }}>{item.leave_code || "-"}</td>
                      {canManageAll && (
                        <td data-label="พนักงาน">
                          <div className="fw-medium">{getEmployeeName(item.employee)}</div>
                          <div className="small text-secondary">{item.employee?.department || "-"}</div>
                        </td>
                      )}
                      <td data-label="ประเภท" className="text-nowrap">{typeLabel}</td>
                      <td data-label="ช่วงวันที่" className="text-nowrap">
                        {format_date(item.date_from, "dd/mm/yyyy")}
                        {item.date_to !== item.date_from ? ` - ${format_date(item.date_to, "dd/mm/yyyy")}` : ""}
                        {item.half_day && item.half_day !== "none" ? (
                          <div className="small text-secondary">{item.half_day === "morning" ? "ครึ่งวันเช้า" : "ครึ่งวันบ่าย"}</div>
                        ) : null}
                      </td>
                      <td data-label="จำนวนวัน" className="text-center fw-semibold">{item.total_days}</td>
                      <td data-label="เหตุผล">
                        <div className="small">{item.reason || "-"}</div>
                        {item.status === "rejected" && item.rejection_reason ? (
                          <div className="small" style={{ color: "#b63636" }}>เหตุผลที่ไม่อนุมัติ: {item.rejection_reason}</div>
                        ) : null}
                      </td>
                      <td data-label="สถานะ" className="text-center text-nowrap">
                        <span className="rounded-pill px-3 py-1 small fw-semibold text-nowrap d-inline-block" style={statusStyle(item.status)}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td data-label="จัดการ" className="text-center text-nowrap">
                        {item.status === "pending" && canManageAll && (
                          <>
                            <button className="btn btn-sm btn-outline-success rounded-3 me-1" title="อนุมัติ" onClick={() => handleApprove(item)}>
                              <i className="fa-solid fa-check" />
                            </button>
                            <button className="btn btn-sm btn-outline-danger rounded-3 me-1" title="ไม่อนุมัติ" onClick={() => handleReject(item)}>
                              <i className="fa-solid fa-xmark" />
                            </button>
                          </>
                        )}
                        {item.status === "pending" && (
                          <button className="btn btn-sm btn-outline-secondary rounded-3 me-1" title="แก้ไข" onClick={() => handleOpenModal(item)}>
                            <i className="fa-solid fa-pen" />
                          </button>
                        )}
                        {["pending", "approved"].includes(item.status) && (
                          <button className="btn btn-sm btn-outline-secondary rounded-3" title="ยกเลิกคำขอ" onClick={() => handleCancel(item)}>
                            <i className="fa-solid fa-ban" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={canManageAll ? 8 : 7} className="text-center text-secondary py-5">ยังไม่มีคำขอลาในปี {year}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pageCount > 1 && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination count={pageCount} page={page} onChange={(event, value) => setPage(value)} color="primary" shape="rounded" />
          </div>
        )}
      </div>

      <LeaveModal
        show={showModal}
        onHide={handleCloseModal}
        editData={editData}
        onSave={handleSave}
        leaveTypes={leaveTypes}
        employeeOptions={employeeOptions}
        canManageAll={canManageAll}
      />
    </AppShell>
  );
}
